import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../../App';
import './accountAdmin.css';

function authHeader() { const t = localStorage.getItem('token'); return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }; }

export default function StudentHistory() {
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [tab, setTab] = useState('class'); // 'class' or 'attendance'
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [grades, setGrades] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsAll, setStudentsAll] = useState([]);
  const [studentQuery, setStudentQuery] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedClass, setSelectedClass] = useState('');

  // helper: map class id to class name when available
  function getClassName(classId) {
    if (!classId) return null;
    const found = (classes || []).find(c => String(c.id) === String(classId));
    if (found) return found.name;
    return null;
  }

  // helper: format datetime/string to date-only (local Vietnamese format)
  function formatDateOnly(dt) {
    if (!dt) return '';
    try {
      // If already a Date object
      const d = (dt instanceof Date) ? dt : new Date(dt);
      if (isNaN(d.getTime())) return String(dt).split('T')[0] || String(dt);
      return d.toLocaleDateString('vi-VN');
    } catch (e) { return String(dt).split('T')[0] || String(dt); }
  }

  // helper: prefer last_name + first_name for people objects, fallback to name or username
  function formatPersonName(p) {
    if (!p) return '';
    // if passed a string username
    if (typeof p === 'string') return p;
    const last = p.last_name || p.lastName || p.lastname || p.family_name || '';
    const first = p.first_name || p.firstName || p.firstname || p.given_name || '';
    const assembled = [last, first].filter(Boolean).join(' ').trim();
    if (assembled) return assembled;
    if (p.name) return p.name;
    if (p.username) return p.username;
    return '';
  }

  // small in-file searchable dropdown (select2-like)
  function SearchableDropdown({ items, value, onChange, placeholder }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [highlight, setHighlight] = useState(0);
    const ref = useRef();

    useEffect(() => {
      function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
      document.addEventListener('mousedown', onDoc);
      return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const filtered = (items || []).filter(it => (q || '').trim() === '' ? true : ((it.name || '').toLowerCase().includes(q.toLowerCase())));

    function handleKey(e) {
      if (!open) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
      if (e.key === 'Enter') { e.preventDefault(); const sel = filtered[highlight]; if (sel) { onChange(sel.id); setOpen(false); } }
      if (e.key === 'Escape') { setOpen(false); }
    }

    return (
      <div className="searchable-dropdown" ref={ref} tabIndex={0} onKeyDown={handleKey}>
        <div className="sd-control" onClick={() => setOpen(o => !o)}>
          <div className="sd-value">{(items || []).find(i => String(i.id) === String(value)) ? ((items || []).find(i => String(i.id) === String(value)).name) : (placeholder || '-- Chọn --')}</div>
          <div className="sd-arrow">▾</div>
        </div>
        {open && (
          <div className="sd-menu">
            <input className="sd-search" placeholder="Tìm..." value={q} onChange={e => { setQ(e.target.value); setHighlight(0); }} />
            <div className="sd-list">
              {filtered.length === 0 ? <div className="sd-empty">Không tìm thấy</div> : filtered.map((it, idx) => (
                <div key={it.id} className={`sd-item ${idx === highlight ? 'highlight' : ''}`} onMouseEnter={() => setHighlight(idx)} onClick={() => { onChange(it.id); setOpen(false); }}>
                  {it.name || `#${it.id}`}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    if (!user) return; (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/admin/users`, { headers: authHeader() });
        if (!res.ok) return;
        const jd = await res.json(); setUsers(jd.users || []);
        // no additional side-effects here
      } catch (e) { console.error(e); }
    })();
  }, [user]);

  // fetch grades on mount
  useEffect(() => {
    if (!user) return; (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/grades`, { headers: authHeader() });
        if (!res.ok) return;
        const jd = await res.json(); setGrades(jd.grades || []);
      } catch (e) { console.error(e); }
    })();
  }, [user]);

  // load selected student's class-history only when a student is selected
  // history is loaded on-demand via "Xem báo cáo" button

  // when a grade is selected, fetch classes and reset downstream selections
  useEffect(() => {
    if (!selectedGrade) { setClasses([]); setSelectedClass(''); setStudents([]); setSelected(null); return; }
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/classes`, { headers: authHeader() });
        if (!res.ok) { setClasses([]); return; }
        const jd = await res.json();
        // filter by grade_id
        const cls = (jd.classes || []).filter(c => Number(c.grade_id) === Number(selectedGrade));
        setClasses(cls);
      } catch (e) { console.error(e); setClasses([]); }
    })();
  }, [selectedGrade, user]);

  // when a class is selected, fetch students for that class (used by attendance tab)
  useEffect(() => {
    if (!selectedClass) { setStudents([]); setSelected(null); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiUrl}/api/classes/${selectedClass}/students`, { headers: authHeader() });
        if (!res.ok) { setStudents([]); setLoading(false); return; }
        const jd = await res.json(); setStudents(jd.students || []);
      } catch (e) { console.error(e); setStudents([]); }
      setLoading(false);
    })();
  }, [selectedClass, user]);

  // when class-history tab is active, fetch all students for searchable selector
  useEffect(() => {
    if (!user) return; if (tab !== 'class') { setStudentsAll([]); setStudentQuery(''); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiUrl}/api/admin/students`, { headers: authHeader() });
        if (!res.ok) { setStudentsAll([]); setLoading(false); return; }
        const jd = await res.json(); setStudentsAll(jd.students || []);
      } catch (e) { console.error(e); setStudentsAll([]); }
      setLoading(false);
    })();
  }, [tab, user]);

  // reset page when history or filters change
  useEffect(() => { setPage(1); }, [history, tab, selectedGrade, selectedClass, selected]);

  // attendance is loaded on-demand via "Xem báo cáo" button

  // helper: fetch class-history filtered by class ids (if provided), or all if none
  async function fetchClassHistoryFiltered(classIds) {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/students/history`, { headers: authHeader() });
      if (!res.ok) { setHistory([]); setLoading(false); return; }
      const jd = await res.json();
      let rows = jd.history || [];
      if (Array.isArray(classIds) && classIds.length) {
        const ids = classIds.map(x => Number(x));
        rows = rows.filter(r => ids.includes(Number(r.from_class_id)) || ids.includes(Number(r.to_class_id)));
      }
      setHistory(rows);
    } catch (e) { console.error(e); setHistory([]); }
    setLoading(false);
  }

  // helper: fetch attendance for a single student
  async function fetchAttendanceForStudent(studentId) {
    try {
      const res = await fetch(`${apiUrl}/api/admin/students/${studentId}/attendance`, { headers: authHeader() });
      if (!res.ok) return null;
      const jd = await res.json();
      return jd.attendance || [];
    } catch (e) { console.error(e); return null; }
  }

  // main handler for the "Xem báo cáo" button: loads either class-history or attendance depending on tab
  async function handleFetchReport() {
    // determine selection level
    // - grade only: selectedGrade set, selectedClass empty
    // - class: selectedClass set
    // - student: selected set
    if (tab === 'class') {
      // class-history view
      if (selected) {
        // single student
        setLoading(true);
        try {
          const res = await fetch(`${apiUrl}/api/admin/students/${selected}/history`, { headers: authHeader() });
          if (!res.ok) { setHistory([]); setLoading(false); return; }
          const jd = await res.json(); setHistory(jd.history || []);
        } catch (e) { console.error(e); setHistory([]); }
        setLoading(false);
        return;
      }
      // no student selected: filter by class(s)
      let classIds = [];
      if (selectedClass) classIds = [selectedClass];
      else if (selectedGrade) classIds = classes.map(c => c.id);
      await fetchClassHistoryFiltered(classIds);
      return;
    }

    // attendance tab
    if (tab === 'attendance') {
      setAttendance([]);
      setLoading(true);
      try {
        if (selected) {
          const att = await fetchAttendanceForStudent(selected);
          setAttendance(att || []);
          setLoading(false);
          return;
        }

        // class-level or grade-level: gather students, then fetch per-student attendance
        let targetClasses = [];
        if (selectedClass) targetClasses = [selectedClass];
        else if (selectedGrade) targetClasses = classes.map(c => c.id);
        const allStudents = [];
        for (const cid of targetClasses) {
          try {
            const res = await fetch(`${apiUrl}/api/classes/${cid}/students`, { headers: authHeader() });
            if (!res.ok) continue;
            const jd = await res.json();
            const studs = jd.students || [];
            studs.forEach(s => allStudents.push({ id: s.id, name: s.name || (`#${s.id}`) }));
          } catch (e) { console.error('failed fetch students for class', cid, e); }
        }

        // fetch attendance per student in parallel (limit concurrency if needed)
        const promises = allStudents.map(s => fetchAttendanceForStudent(s.id).then(a => ({ student: s, attendance: a || [] })));
        const results = await Promise.all(promises);
        setAttendance(results);
      } catch (e) { console.error(e); setAttendance([]); }
      setLoading(false);
      return;
    }
  }

  return (
    <div className="admin-wrap">
      <h2>Student History</h2>
      <div className="card">
        <div className="tabs">
          <button className={tab === 'class' ? 'active' : ''} onClick={() => setTab('class')}>Class History</button>
          <button className={tab === 'attendance' ? 'active' : ''} onClick={() => setTab('attendance')}>Attendance History</button>
        </div>
        {tab === 'attendance' ? (
          <>
            <div className="filters-inline mt-8">
              <div className="filter-select">
                <label>Grades</label>
                <select value={selectedGrade || ''} onChange={e => { setSelectedGrade(e.target.value); setSelectedClass(''); setStudents([]); setSelected(null); }}>
                  <option value="">-- Select Grade --</option>
                  {grades.map(g => (<option key={g.id} value={g.id}>{g.name}</option>))}
                </select>
              </div>
              <div className="filter-select">
                <label>Classes</label>
                <select value={selectedClass || ''} onChange={e => { setSelectedClass(e.target.value); setSelected(null); }}>
                  <option value="">-- Select Class --</option>
                  {classes.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div className="filter-select">
                <label>Students</label>
                <select value={selected || ''} onChange={e => setSelected(e.target.value)}>
                  <option value="">-- Select Student --</option>
                  {students.map(s => (<option key={s.id} value={s.id}>{formatPersonName(s) || s.name || `#${s.id}`}</option>))}
                </select>
              </div>
              <div className="view-report-section">
                <button className="btn-primary-view" onClick={handleFetchReport} disabled={!selectedGrade && !selectedClass && !selected}>View Report</button>
              </div>
            </div>
          </>
        ) : (
          // class-history tab: show grade/class filters + single searchable student selector
          <>
            <div className="filters-inline mt-8">
              <div className="filter-select">
                <label>Grades</label>
                <select value={selectedGrade || ''} onChange={e => { setSelectedGrade(e.target.value); setSelectedClass(''); setStudents([]); setSelected(null); }}>
                  <option value="">-- Select Grade --</option>
                  {grades.map(g => (<option key={g.id} value={g.id}>{g.name}</option>))}
                </select>
              </div>
              <div className="filter-select">
                <label>Classes</label>
                <select value={selectedClass || ''} onChange={e => { setSelectedClass(e.target.value); setSelected(null); }}>
                  <option value="">-- Select Class --</option>
                  {classes.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div className="filter-select">
                <label>Students</label>
                <select value={selected || ''} onChange={e => setSelected(e.target.value)}>
                  <option value="">-- Select Student --</option>
                  {students.map(s => (<option key={s.id} value={s.id}>{formatPersonName(s) || s.name || `#${s.id}`}</option>))}
                </select>
              </div>
              <div className="view-report-section">
                <button className="btn-primary-view" onClick={handleFetchReport} disabled={!selectedGrade && !selectedClass && !selected}>View Report</button>
              </div>
            </div>
          </>
        )}

        <div className="mt-12">
          {loading ? <div className="muted">Loading...</div> : (
            tab === 'class' ? (
              <table className="aa-data-table">
                <thead><tr><th>No.</th><th>Student</th><th>From Class</th><th>To Class</th><th>Changed At</th><th>Changed By</th></tr></thead>
                <tbody>
                  {history.length === 0 ? <tr><td colSpan={6} className="muted">No history available</td></tr> : (
                        (() => {
                          // sort newest-first by changed_at if present, otherwise by numeric id
                          const sorted = (history || []).slice().sort((a, b) => {
                            if (a.changed_at && b.changed_at) {
                              const da = new Date(a.changed_at).getTime() || 0;
                              const db = new Date(b.changed_at).getTime() || 0;
                              return db - da;
                            }
                            return (Number(b.id) || 0) - (Number(a.id) || 0);
                          });
                          const total = sorted.length || 0;
                          const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
                          const currentPage = Math.min(Math.max(1, page), totalPages);
                          const start = (currentPage - 1) * PAGE_SIZE;
                          const pageItems = sorted.slice(start, start + PAGE_SIZE);
                          return (
                            <>
                              {pageItems.map((h, idx) => {
                                const overallIdx = start + idx;
                                return (
                                  <tr key={h.id}>
                                    <td>{total - overallIdx}</td>
                                    <td>{(
                                      // prefer explicit last_name + first_name on the history row
                                      (h.student_last_name || h.student_first_name) ? [h.student_last_name || '', h.student_first_name || ''].filter(Boolean).join(' ') :
                                        // then prefer a provided student_name
                                        (h.student_name ? h.student_name : (
                                          // else try to lookup in studentsAll loaded for class-history
                                          (studentsAll || []).find(s => String(s.id) === String(h.student_id)) ? formatPersonName((studentsAll || []).find(s => String(s.id) === String(h.student_id))) : (h.student_id ? ('#' + h.student_id) : <b>Unknow</b>)
                                        ))
                                    )}</td>
                                    <td>{h.from_class_name || h.from_class_id || <b><i>No Class</i></b>}</td>
                                    <td>{h.to_class_name || h.to_class_id || <b><i>No Class</i></b>}</td>
                                    <td>{formatDateOnly(h.changed_at) || <b><i>No information</i></b>}</td>
                                    <td>{(
                                      (h.changed_by_last_name || h.changed_by_lastname || h.changed_by_last) || (h.changed_by_first_name || h.changed_by_first) ? [h.changed_by_last_name || h.changed_by_lastname || h.changed_by_last || '', h.changed_by_first_name || h.changed_by_first || ''].filter(Boolean).join(' ') : (h.changed_by_username || h.changed_by || '')
                                    ) || <b><i>Unknown</i></b>}</td>
                                  </tr>
                                );
                              })}

                              {/* pagination controls */}
                              {total > PAGE_SIZE && (
                                <tr><td colSpan={6} className="pagination-row">
                                  <div className="pagination">
                                    <button className="btn-link" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}>Prev</button>
                                    {[...Array(totalPages)].map((_, i) => (
                                      <button key={i} className={`btn-link ${currentPage === (i + 1) ? 'active-nav-page' : ''}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
                                    ))}
                                    <button className="btn-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
                                  </div>
                                </td></tr>
                              )}
                            </>
                          );
                        })()
                      )}
                </tbody>
              </table>
            ) : (
              <div>
                {(!attendance || attendance.length === 0) ? <div className="muted">No attendance history available</div> : (
                  // attendance can be either:
                  // 1) array of attendance-history rows (for single student) -> each item has id, to_class_id, attendance_history
                  // 2) array of { student, attendance } objects (for aggregated class/grade) -> student: {id,name}, attendance: []
                  Array.isArray(attendance) && attendance.length && attendance[0].student ? (
                    // aggregated per-student results
                    attendance.map(item => (
                      <div key={item.student.id} className="attendance-card">
                        <h4>{formatPersonName(item.student) || item.student.name} <small className="muted">(ID: {item.student.id})</small></h4>
                        {(!item.attendance || item.attendance.length === 0) ? <div className="muted">No attendance data available</div> : (
                          item.attendance.map(a => (
                            <div key={a.id} className="mb-8">
                              <h5>Class: {getClassName(a.to_class_id) || a.to_class_id || <b>Unknow</b>} <small className="muted">(changed at: {formatDateOnly(a.changed_at)})</small></h5>
                              <table className="aa-data-table">
                                <thead><tr><th>Week</th><th>Date</th><th>Status</th><th>Note</th></tr></thead>
                                <tbody>
                                  {a.attendance_history.map((ah, i) => (
                                    <tr key={i}><td>{ah.week}</td><td>{formatDateOnly(ah.date) || <b>Unknow</b>}</td><td>{ah.status || <b>Unknow</b>}</td><td>{ah.note || ''}</td></tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ))
                        )}
                      </div>
                    ))
                  ) : (
                    // single-student format (array of rows)
                    attendance.map(a => (
                      <div key={a.id} className="attendance-card">
                        <h4>Class: {getClassName(a.to_class_id) || a.to_class_id || <b>Unknow</b>} <small className="muted">(changed at: {formatDateOnly(a.changed_at)})</small></h4>
                        <table className="aa-data-table">
                          <thead><tr><th>Week</th><th>Date</th><th>Status</th><th>Note</th></tr></thead>
                          <tbody>
                            {a.attendance_history.map((ah, i) => (
                              <tr key={i}><td>{ah.week}</td><td>{formatDateOnly(ah.date) || <b>Unknow</b>}</td><td>{ah.status || <b>Unknow</b>}</td><td>{ah.note || ''}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))
                  )
                )}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
